import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface EyebrowProps {
    children: ReactNode
    className?: string
    centered?: boolean
}

interface SectionHeadingProps {
    eyebrow?: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    as?: 'h1' | 'h2'
    id?: string
    className?: string
}

export const Eyebrow: FC<EyebrowProps> = ({ children, className, centered = false }): ReactNode => {
    return (
        <p
            className={cn(
                'flex items-center gap-3 text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-brass',
                centered && 'justify-center',
                className
            )}
        >
            <span aria-hidden='true' className='h-px w-8 bg-brass/60' />
            {children}
            {centered && <span aria-hidden='true' className='h-px w-8 bg-brass/60' />}
        </p>
    )
}

export const displayTitleClass = 'font-heading font-medium uppercase leading-[1.04] tracking-[0.01em] text-balance text-foreground'

const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    align = 'left',
    as = 'h2',
    id,
    className
}): ReactNode => {
    const Heading = as
    const centered = align === 'center'

    return (
        <div className={cn('flex flex-col gap-5', centered && 'items-center text-center', className)}>
            {eyebrow && <Eyebrow centered={centered}>{eyebrow}</Eyebrow>}

            <Heading
                id={id}
                className={cn(
                    displayTitleClass,
                    as === 'h1' ? 'text-4xl sm:text-5xl lg:text-6xl xl:text-7xl' : 'text-4xl sm:text-5xl lg:text-[3.5rem]',
                    centered ? 'max-w-4xl' : 'max-w-3xl'
                )}
            >
                {title}
            </Heading>

            {description && (
                <div className={cn('text-base leading-relaxed text-muted-foreground sm:text-lg', centered ? 'max-w-2xl' : 'max-w-2xl')}>
                    {description}
                </div>
            )}
        </div>
    )
}

export default SectionHeading
